import { EvchargeStrategy } from "../models";
import { EvChDAY, EvChSESSIONInDay, Inverter } from "../telemetry";

export const MathSomme = (arr: number[]) => arr.reduce((a, b) => a + b, 0);
export const MathAvg = (arr: number[]) => MathSomme(arr) / arr.length;
export const countValuesDifferentToZero = (arr: number[]) =>
  arr.reduce((total, current) => current !== 0 ? (total + 1) : total, 0);
export const MathAvgWithValuesDifferentToZero = (arr: number[]) =>
  countValuesDifferentToZero(arr) === 0
    ? 0
    : (MathSomme(arr) / countValuesDifferentToZero(arr));

export const randomString = () => Math.random().toString().substring(2, 8);
export const toFixedIfNecessary = (value: string, dp: number) => +parseFloat(value).toFixed(dp);
export const timeout = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const minToZero = <T extends { [key: string]: number }>(r: T): T => {
  const rCopy = { ...r };
  const minThreshold: any = 0;
  for (const key in rCopy) {
    if (Object.prototype.hasOwnProperty.call(rCopy, key)) {
      rCopy[key] = rCopy[key] > 0 ? rCopy[key] : minThreshold;
    }
  }
  return rCopy;
};

export const requestAnimationFramePromise = () => {
  return new Promise<number>(resolve => {
    requestAnimationFrame(timestamp => resolve(timestamp));
  });
}

export const belowThresholdFillWithZero = <T extends { [key: string]: number }>(r: T, threshold: number, keyException: string[] = []): T => {
  const rCopy = { ...r };
  const zero: any = 0;
  for (const key in rCopy) {
    if (Object.prototype.hasOwnProperty.call(rCopy, key)) {
      if (!keyException.includes(key)) {
        rCopy[key] = rCopy[key] > threshold ? rCopy[key] : zero;
      }
    }
  }
  return rCopy;
};

export const minElementToZero = (element: number) => element > 0 ? element : 0;

export const minElementToNull = (element: number) => element > 0 ? element : null;

export const objectPropertiesToString = (o: object) => {
  (Object.keys(o) as Array<keyof typeof o>).forEach((k) => {
    if (typeof o[k] === 'object') {
      return objectPropertiesToString(o[k]);
    }

    return ((o[k] as any) = '' + o[k]);
  });

  return o;
};

export const applyFnToProperty = (data: Array<Partial<Inverter>>, fn: <T>(t: T[], key: keyof Inverter) => T) => {
  if (data.length === 0) {
    return {};
  }
  const model: Inverter = JSON.parse(JSON.stringify(data[0]));
  const modelKeys = Object.keys(model) as Array<keyof typeof model>;
  const result = {} as Partial<Inverter>;
  modelKeys.forEach((key) => {
    const values = data.map((d) => d[key]);
    result[key] = fn(values, key) as any;
  });
  return result;
};

export const generateTransactionId = (): string =>
  `${Date.now()}${Math.floor(Math.random() * 1000)}`;

export const generateUUID = () => {
  let d = new Date().getTime();
  // Time in microseconds since page-load or 0 if unsupported
  let d2 = ((typeof performance !== 'undefined') && performance.now && (performance.now()*1000)) || 0;
  return 'xxxxxxxx-xxxx'.replace(/[xy]/g, (c) => {
      //random number between 0 and 16
      let r = Math.random() * 16;
      //Use timestamp until depleted
      if(d > 0){
          r = (d + r)%16 | 0;
          d = Math.floor(d/16);
      //Use microseconds since page-load if supported
      } else {
          r = (d2 + r)%16 | 0;
          d2 = Math.floor(d2/16);
      }
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

export const smartWrap = (str: string, wrapLen: number) => {
  const words: string[] = str.split(' ');
  let lines = [];
  let line = '';

  for(let word of words) {
    if (line.length + word.length > wrapLen) {
      lines.push(line.trim());
      line = '';
    }
    line += line ? ' ' + word : word;
  }
  lines.push(line.trim());

  return lines.join('\n');
}

export const balancesContraryPowers = (px: number, py: number) => {
  if (px > 0 && py > 0) {
    if (px > py) {
      return [`${px - py}`, '0'];
    } else {
      return ['0', `${py - px}`];
    }
  }
  return [`${px}`, `${py}`];
}

export const groupByCount = (values: any[], count: number) =>
  values.reduce((prev: Array<any[]>, currentValue) => {
    const data = [...prev];
    const currentIndex = data.length - 1;
    if (data[currentIndex].length < count) {
      data[currentIndex].push(currentValue);
    } else {
      data.push([currentValue]);
    }
    return data;
  }, [[]]);

export const getPlaceholdersForEmptyDays = (optionsArr: any[] ) => {
  if (optionsArr.length === 2) {
    const firstArr: any = optionsArr[0];
    const secondArr: any = optionsArr[1];

    for (let index = 0; index < secondArr.data.length; index++) {
      const value1 = firstArr.data[index].value;
      const value2 = secondArr.data[index].value;

      if(value1 === null && value2 !== null){
        secondArr.data[index].value = null
      } else if(value1 !== null && value2 === null){
        firstArr.data[index].value = null
      }
    }
    return [firstArr, secondArr];
  }
  return optionsArr;
};

export const groupBy = <T>(series: T[], filter: (curr: T) => string | undefined) =>
  series.reduce((prev: { [key: string]: T[] }, curr: T) => {
    const key = filter(curr);
    if (key && prev[key]) {
      prev[key].push(curr);
    } else if (key) {
      prev[key] = [curr];
    }
    return prev;
  }, {});

export const getTotalValuesFromDailySessions = (sessions: EvChSESSIONInDay[], powerProfile: EvchargeStrategy) =>
  sessions.filter((session) => session.PP === powerProfile).reduce(
    (prev, curr) => ({
      CT: prev.CT + curr.CT,
      SEM: prev.SEM + curr.SEM,
    }), { CT: 0, SEM: 0},
  );

export const getTotalValuesFromMonthSessions = (evChDays: Partial<EvChDAY>[], powerProfile: EvchargeStrategy) => {
  let aggregate = { CT: 0, SEM: 0};
  for (const evChDay of evChDays) {
    aggregate = (evChDay.TIDA || [])?.filter((session) => session.PP === powerProfile).reduce(
      (prev, curr) => ({
        CT: prev.CT + curr.CT,
        SEM: prev.SEM + curr.SEM,
      }), aggregate,
    );
  }
  return aggregate;
};

export const getStrategyName = (strategyCode: number) => {
  if (strategyCode === EvchargeStrategy.Fast) {
    return 'Fast';
  }
  else if (strategyCode === EvchargeStrategy.Green) {
    return 'Green';
  }
  else if (strategyCode === EvchargeStrategy.Km_repeat) {
    return 'Kilometer';
  }
  else if (strategyCode === EvchargeStrategy.Green_mixed) {
    return 'Green mixed';
  }
  else if (strategyCode === EvchargeStrategy.Km_single) {
    return 'Single kilometer';
  }
  else if (strategyCode === EvchargeStrategy.Time_power) {
    return 'Time Power';
  }
  else if (strategyCode === EvchargeStrategy.Time_power_single) {
    return 'Single Time Power';
  }
  else if (strategyCode === EvchargeStrategy.Zero_emissions) {
    return 'Zero Emissions';
  }
  return '';
};

export const getCreatedAtFromTID = (TID: string, series: string) => +TID.replace(series, '') * 1000;

export const clone = <T>(obj: T): T => JSON.parse(JSON.stringify(obj));

export const buildMinFirmwareVersionConstraint = (minFirmwareVersion: string) => {
  return  (firmwareVersion: string) => {
    const minFirmwareVersionParts = minFirmwareVersion.split('.');
    const firmwareVersionParts = firmwareVersion.split('.');
    for (let i = 0; i < minFirmwareVersionParts.length; i++) {
      if (parseInt(firmwareVersionParts[i]) < parseInt(minFirmwareVersionParts[i])) {
        return false;
      }
    }
    return true;
  }
}

export const print = (message: string) => console.warn(`${new Date().toISOString()} - ${message}`);

export const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

/** Converts to locale string defaults to en-EN */
export const getLocaleString = (value: number, locale?: string) => {
  try {
    return value.toLocaleString(locale || 'en-EN')
  } catch {
    return value.toLocaleString('en-EN')
  }
};
